import i18next from 'i18next';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Icon, Input, Modal, Select } from '@scuf/common';
import './HierarchyBuilder.scss';
import HierarchyBuilderEditSidePanel from './HierarchyBuilderEditSidePanel';
import {
  Column,
  HierarchyTable,
  TreeNode,
  TreeTableHeader
} from '../../../commonUtils/HierarchyTable';

import { v4 as uuid4 } from 'uuid';

import dragIcon from '../../../../assets/drag-and-drop.svg';
import { SplitPanelUtil } from '../../../commonUtils/SplitPanelUtil';
import {
  APIContext,
  consumeAPIs,
  IAPIPackage
} from '../../../commonUtils/APIContext';
import {
  IHierarchyBuilderNodeData,
  INode,
  INodeMetadata,
  INodeMetadataResponse,
  INodeMetadatum,
  INodeResponse,
  IOrganisationResponse
} from '../../onboardingModels';
import useFetch, { IUseFetch } from '../../../commonUtils/useFetch';
import toasts from '../../../commonUtils/toasts';
import authService from '../../../../authService';
import {
  ContextMenu,
  ContextMenuWrapper
} from '../../../commonUtils/ContextMenu';
import {
  getNodePath,
  isNodeRootNode,
  nodeTypes,
  pascalToCamelCase
} from '../../onboardingUtils';

const lazyRowBufferDistance = 5;

interface IHierarchyBuilder extends IAPIPackage {}

export const HierarchyBuilder = (props: IHierarchyBuilder) => {
  const apiPackage = useContext(APIContext);
  let history = useHistory();

  const globalAbortControllerRef = useRef<AbortController>(
    new AbortController()
  );

  const { signal } = globalAbortControllerRef.current;

  // const [clipboardNodes, setClipboardNodes] = useState<TreeNode[]>([]);

  const [createNewOrgNameField, setCreateNewOrgNameField] = useState<string>(
    ''
  );
  const [createNewOrgModelOpen, setCreateNewOrgModelOpen] = useState<boolean>(
    false
  );
  const [createNewOrgModelSending, setCreateNewOrgModelSending] = useState<
    boolean
  >(false);

  const [addNewNodesOpen, setAddNewNodesOpen] = useState<boolean>(false);
  const [addNewNodesOpenSending, setAddNewNodesOpenSending] = useState<boolean>(
    false
  );

  const [addNewNodeNameField, setAddNewNodeNameField] = useState<string>('');

  /**
   * Only allow nodes of one root org to be here, no cross org selecting
   * For new node, will only use the first node
   */
  const [selectedNodes, setSelectedNodes] = useState<
    TreeNode<IHierarchyBuilderNodeData>[]
  >([]);

  const [newNodeType, setNewNodeType] = useState<string>();

  const tranformOrgDataToTreeNode = (
    rootOrganisationResponse: IOrganisationResponse
  ) => {
    return rootOrganisationResponse.resources.map(org => {
      let treeNode: TreeNode<IHierarchyBuilderNodeData>;
      treeNode = {
        key: org.id,
        children: [],
        leaf: org.childCount === 0,
        data: { ...org, rootNodeId: org.id, path: [] }
      };
      return treeNode;
    });
  };

  const [
    rootOrganisationData,
    isLoadingRootOrganisationData,
    errorRootOrganisationData,
    reFetchRootOrganisationData
  ]: IUseFetch<TreeNode[]> = useFetch<IOrganisationResponse>(
    `${apiPackage.baseURL}/api/RootOrganisations/${apiPackage.offering}`,
    apiPackage.authFetch,
    undefined,
    false,
    tranformOrgDataToTreeNode
  );

  useEffect(() => {
    reFetchRootOrganisationData();
  }, []);

  useEffect(() => {
    if (errorRootOrganisationData) {
      toasts.error('Error getting organisations');
    }
  }, [errorRootOrganisationData]);

  const [treeNodeHierarchy, setTreeNodeHierarchy] = useState<
    TreeNode<IHierarchyBuilderNodeData>[]
  >([]);

  useEffect(() => {
    setTreeNodeHierarchy(rootOrganisationData);
  }, [rootOrganisationData]);

  const onExpandRow = (rowExpanding: {
    originalEvent: Event;
    node: TreeNode<IHierarchyBuilderNodeData>;
  }): void => {
    if (rowExpanding.node.children.length === 0) {
      fetchChildrenAndInsert(rowExpanding.node);
    }
  };

  // Metadata API doesnt actually give name and type properties, so we have to call this too and slap that back into metadata.
  const refetchNodeTypeAndName = (rootNodeId: string, nodeId: string) => {
    apiPackage
      .authFetch(
        `${apiPackage.baseURL}/api/Organisations/${apiPackage.offering}/${rootNodeId}/${nodeId}`
      )
      .then(res => res.json() as Promise<INode>)
      .then(node => {
        setNodeMetadata(prevMetadataState => {
          const editedMetadata = prevMetadataState.find(
            metadata => metadata.id === nodeId
          );
          if (editedMetadata) {
            const newNameObj = {
              key: 'Name',
              value: node.name
            };
            const foundNameIndex = editedMetadata.data.findIndex(
              nodeData => nodeData.key === 'Name'
            );
            if (foundNameIndex > -1) {
              editedMetadata.data[foundNameIndex] = newNameObj;
            } else {
              editedMetadata.data.push(newNameObj);
            }

            const newTypeObj = {
              key: 'Type',
              value: node.type
            };
            const foundTypeIndex = editedMetadata.data.findIndex(
              nodeData => nodeData.key === 'Type'
            );
            if (foundTypeIndex > -1) {
              editedMetadata.data[foundTypeIndex] = newTypeObj;
            } else {
              editedMetadata.data.push(newTypeObj);
            }

            const newState = [
              ...prevMetadataState.filter(obj => obj.id !== nodeId),
              ...(editedMetadata ? [editedMetadata] : [])
            ];
            return newState;
          }
          return prevMetadataState;
        });

        // const newNode = {
        //   key: node.id,
        //   children: [],
        //   leaf: node.childCount === 0,
        //   data: {
        //     ...node,
        //     rootNodeId: data.rootNodeId,
        //     path: [
        //       ...data.path,
        //       ...(data.id === data.rootNodeId ? [] : [data.id])
        //     ]
        //   }
        // }
      });
  };

  const fetchChildrenAndInsert = (
    parentNode: TreeNode<IHierarchyBuilderNodeData>
  ) => {
    if (parentNode.data) {
      const data = parentNode.data;

      apiPackage
        .authFetch(
          `${apiPackage.baseURL}/api/Organisations/${apiPackage.offering}/${data.rootNodeId}/${data.id}/Organisations`,
          {
            signal
          }
        )
        .then(res => res.json() as Promise<INodeResponse>)
        .then(childrenNodeResponse => {
          const childrenNodes: TreeNode[] = childrenNodeResponse.resources.map(
            node => ({
              key: node.id,
              children: [],
              leaf: node.childCount === 0,
              data: {
                ...node,
                rootNodeId: data.rootNodeId,
                path: [
                  ...data.path,
                  ...(data.id === data.rootNodeId ? [] : [data.id])
                ]
              }
            })
          );
          childrenNodes.forEach(node => {
            fetchMetaData(node.data.rootNodeId, node.data.id);
          });
          addToHierarchy(data.id, childrenNodes);
        })
        .catch(e => {
          if (!signal.aborted) {
            console.error(`${i18next.t('childNodesError')}: ${e}`);
            toasts.error(i18next.t('childNodesError'));
          }
        });
    }
  };

  const addToHierarchy = (targetId: string, children: TreeNode[]) => {
    if (treeNodeHierarchy) {
      setTreeNodeHierarchy(prevState =>
        prevState.map(node => putChildrenInHierarchy(targetId, node, children))
      );
    }
  };

  const putChildrenInHierarchy = (
    targetId: string,
    hierarchyNode: TreeNode,
    children: Array<TreeNode>
  ) => {
    if (hierarchyNode.key === targetId) {
      hierarchyNode.children = [...children, ...hierarchyNode.children];
    } else if (hierarchyNode.children) {
      hierarchyNode.children = hierarchyNode.children.map(child =>
        putChildrenInHierarchy(targetId, child, children)
      );
    }
    return hierarchyNode;
  };

  const createNewOrganisation = () => {
    setCreateNewOrgModelSending(true);
    authService
      .authFetch(
        `${apiPackage.baseURL}/api/v1/root-organisations/${apiPackage.offering}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: createNewOrgNameField,
            type: 'Organisation',
            logoUri: '',
            externalId: ''
          })
        }
      )
      .then(res => {
        if (res.ok) {
          setCreateNewOrgModelOpen(false);
          reFetchRootOrganisationData();
          setCreateNewOrgNameField('');
        } else {
          throw new Error(
            'Something went wrong whilst creating new organisation'
          );
        }
      })
      .catch(e => {
        toasts.error(
          'An error occurred when attempting to create new organisation'
        );
        setCreateNewOrgModelSending(false);
      });
  };

  const onClickNodeCell = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    rowData: TreeNode<IHierarchyBuilderNodeData>
  ) => {
    // Only allow selecting nodes from one one root node at a time
    if (rowData.data?.rootNodeId !== selectedNodes[0]?.data?.rootNodeId) {
      setSelectedNodes([rowData]);
    } else if (event.ctrlKey) {
      if (isNodeSelected(rowData)) {
        setSelectedNodes(prev =>
          prev.filter(nodeRowData => nodeRowData.data?.id !== rowData.data?.id)
        );
      } else {
        setSelectedNodes(prev => [...prev, rowData]);
      }
    } else {
      if (isNodeSelected(rowData) && selectedNodes.length === 1) {
        setSelectedNodes([]);
      } else {
        setSelectedNodes([rowData]);
      }
    }
  };

  const addNewNode = (parentRowData: TreeNode<IHierarchyBuilderNodeData>) => {
    const isRootNode =
      parentRowData.data?.id === parentRowData.data?.rootNodeId;
    setAddNewNodesOpenSending(true);

    // Commented sections in this function are ready for the switch to the JSON PATCH endpoint, which currently isnt ready.

    // const newNode = {
    //   Name: addNewNodeNameField,
    //   Type: newNodeType
    //   // ParentId: isRootNode ? null : parentRowData.data.id
    // };

    const newNode = {
      name: addNewNodeNameField,
      type: newNodeType,
      logoUri: '',
      externalId: '',
      parentId: isRootNode ? null : parentRowData.data?.id
    };

    const pathStr = `${getNodePath(parentRowData)}/${uuid4()}`;

    // const reqBody = JSON.stringify([
    //   { op: 'add', path: pathStr, value: newNode }
    // ]);
    const reqBody = JSON.stringify(newNode);

    authService
      .authFetch(
        // `${apiPackage.baseURL}/api/v1/organisations/${apiPackage.offering}/${parentRowData.data?.rootNodeId}`,
        `${apiPackage.baseURL}/api/Organisations/${apiPackage.offering}/${parentRowData.data?.rootNodeId}`,
        {
          // method: 'PATCH',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: reqBody
        }
      )
      .then(res => {
        if (res.ok) {
          setAddNewNodesOpen(false);
          setAddNewNodesOpenSending(false);

          /** res example
           *
           * authorizations: null
           * childCount: 0
           * childNodes: []
           * externalId: null
           * id: "1428573b-283e-4d77-b164-23494b410547"
           * logoUri: null
           * metadata: null
           * name: "Test Node 20"
           * parent: null
           * parentId: null
           * type: "Organisation"
           */

          res.json().then((resNewNode: INode) => {
            const newNodeGhost: TreeNode = {
              key: resNewNode.id,
              children: [],
              leaf: resNewNode.childCount === 0,
              data: {
                ...resNewNode,
                rootNodeId: parentRowData.data?.rootNodeId,
                path: [
                  ...(parentRowData.data?.path ? parentRowData.data?.path : []),
                  ...(parentRowData.data?.id === parentRowData.data?.rootNodeId
                    ? []
                    : [parentRowData.data?.id])
                ]
              }
            };

            addToHierarchy(
              parentRowData.data?.id ? parentRowData.data?.id : '',
              [newNodeGhost]
            );
          });
        } else {
          throw new Error(
            'Something went wrong whilst trying to add new nodes'
          );
        }
      })
      .catch(e => {
        toasts.error('An error occurred when attempting to add new nodes');
        setAddNewNodesOpenSending(false);
      });
  };

  const isNodeSelected = (rowData: TreeNode) => {
    return selectedNodes.find(
      nodeRowData => nodeRowData.data?.id === rowData.data.id
    );
  };

  const onDragNodeCell = (
    event: React.DragEvent<HTMLDivElement>,
    rowData: TreeNode
  ) => {
    if (
      !selectedNodes.find(
        selectedNode => rowData.data.id === selectedNode.data?.id
      )
    ) {
      setSelectedNodes(prev => {
        return [...selectedNodes, rowData];
      });

      event.dataTransfer.setData(
        'text',
        JSON.stringify([...selectedNodes, rowData])
      );
    } else {
      event.dataTransfer.setData('text', JSON.stringify(selectedNodes));
    }
  };

  const onDropNodeCell = (
    event: React.DragEvent<HTMLDivElement>,
    rowData: TreeNode<IHierarchyBuilderNodeData>
  ) => {
    setSelectedNodes([]);
    const droppedNodes: TreeNode<IHierarchyBuilderNodeData>[] = JSON.parse(
      event.dataTransfer.getData('text')
    );

    // must be from the same root node, no cross org moving
    if (droppedNodes[0].data?.rootNodeId === rowData.data?.rootNodeId) {
      postMoveNode(droppedNodes, rowData).then(res => {
        if (res.ok) {
          droppedNodes.forEach(node => {
            moveNodeLocally(node, rowData, droppedNodes);
          });
        } else {
          toasts.error('Couldnt move node(s)');
        }
      });
    }
  };

  const moveNodeLocally = (
    node: TreeNode<IHierarchyBuilderNodeData>,
    newParentNode: TreeNode<IHierarchyBuilderNodeData>,
    duplicateChildrenToRemove: TreeNode<IHierarchyBuilderNodeData>[]
  ) => {
    if (!isNodeRootNode(node) && newParentNode.data) {
      removeNodeFromHierarchy(node);
      duplicateChildrenToRemove.forEach(duplicateChildNode => {
        node = recursivelyRemoveNode(
          duplicateChildNode,
          node.data?.path ? node.data?.path : [],
          node
        );
      });

      node = regeneratePathsLocally(node, newParentNode);
      addToHierarchy(newParentNode.data?.id, [node]);
    }
  };

  const regeneratePathsLocally = (
    node: TreeNode<IHierarchyBuilderNodeData>,
    parentNode: TreeNode<IHierarchyBuilderNodeData>
  ) => {
    if (parentNode.data && node.data) {
      node.data.path = [...parentNode.data.path];
      if (!isNodeRootNode(parentNode)) {
        node.data.path.push(parentNode.data.id);
      }
      node.leaf = node.children.length === 0;
      node.children = node.children.map(childNode => {
        return regeneratePathsLocally(childNode, node);
      });
    }
    return node;
  };

  const postMoveNode = (
    nodes: TreeNode<IHierarchyBuilderNodeData>[],
    targetNode: TreeNode<IHierarchyBuilderNodeData>
  ) => {
    // Current assumption is that you can only select nodes in one org at a time.
    const rootNode = nodes[0].data?.rootNodeId;
    const patchReqContent = nodes.map(node => {
      // Current location
      const fromPathString = getNodePath(node);

      // New location
      const toPathString = `${getNodePath(targetNode)}/${node.data?.id}`;

      // JSON patch op body
      const opObj = {
        op: 'move',
        from: fromPathString,
        path: toPathString
      };
      return opObj;
    });

    return authService.authFetch(
      `${authService.BASE_URL}/api/v1/organisations/LCBS/${rootNode}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(patchReqContent)
      }
    );
  };

  const isNewNodeNameFieldValid = () => {
    // Just tests if string is neither empty nor whitespace
    return !!addNewNodeNameField.trim();
  };

  const isNodeDraggable = (node: TreeNode<IHierarchyBuilderNodeData>) => {
    return !isNodeRootNode(node);
  };

  const nodeCell = (cellData: string, rowData: TreeNode) => {
    return (
      <>
        <div
          className={`node-cell ${
            isNodeSelected(rowData) ? 'selected-node' : ''
          } `}
          draggable={isNodeDraggable(rowData)}
          onDragOver={event => {
            event.preventDefault();
          }}
          onDrop={event => {
            onDropNodeCell(event, rowData);
          }}
          onDragStart={event => {
            onDragNodeCell(event, rowData);
          }}
          onClick={event => onClickNodeCell(event, rowData)}
          onContextMenu={event => {
            console.log('id: ', rowData.data.id);
            handleRightClick(event, rowData);
          }}
        >
          {isNodeDraggable(rowData) ? (
            <>
              <div className="drag-icon-wrapper">
                <img
                  alt="Hold to drag"
                  src={dragIcon}
                  className="drag-handle-icon"
                />
              </div>
              <hr className="vertical-divider" />
            </>
          ) : null}

          <div className="node-icon-wrapper">
            <Icon root="common" size="small" name="buildings" />
          </div>
          <div className="node-name">{cellData}</div>
        </div>
      </>
    );
  };

  const handleBadlyNamedProperties = (propertyArray: Array<INodeMetadatum>) => {
    interface IRemovalListItem {
      initial: string;
      transformed: string;
    }

    const removalList: IRemovalListItem[] = [
      {
        initial: 'SiteAddress',
        transformed: 'Address'
      }
    ];

    return propertyArray.map(propertyObj => {
      const foundMatch = removalList.find(
        removalItem => propertyObj.key === removalItem.initial
      );
      if (foundMatch) {
        return {
          key: foundMatch.transformed,
          value: propertyObj.value
        };
      } else {
        return propertyObj;
      }
    });
  };

  const injectMetaData = (hierarchy: TreeNode[]) => {
    return (hierarchy ? hierarchy : []).map(node => {
      const matchingMetaData = nodeMetaData.find(
        nodeMetaDatum => node.key === nodeMetaDatum.id
      );

      const tableReadyMetadata = matchingMetaData
        ? handleBadlyNamedProperties(matchingMetaData.data).reduce(
            (ob, currentVal) => ({
              ...ob,
              [pascalToCamelCase(currentVal.key)]: currentVal.value
            }),
            {}
          )
        : {};
      // this order is important, as metadata can be more up to date than the hierarchy node data.
      node.data = { ...node.data, ...tableReadyMetadata };

      if (node.children) {
        node.children = injectMetaData(node.children);
      }

      return node;
    });
  };

  const fetchMetaData = (rootNodeId: string, nodeId: string) => {
    return props
      .authFetch(
        rootNodeId === nodeId
          ? `${props.baseURL}/api/v1/metadata/${props.offering}/${rootNodeId}`
          : `${props.baseURL}/api/v1/metadata/${props.offering}/${rootNodeId}/${nodeId}`,
        {
          signal
        }
      )
      .then(res => {
        if (res.ok) {
          return res;
        } else {
          throw new Error(i18next.t('hierarchyMetaError'));
        }
      })
      .then(res => res.json() as Promise<INodeMetadataResponse>)
      .then(metaData => {
        const metaDataObject: INodeMetadata = {
          id: nodeId,
          data: metaData
        };
        setNodeMetadata(prevMetadataState => [
          ...prevMetadataState.filter(obj => obj.id !== nodeId),
          metaDataObject
        ]);
      })
      .catch(e => {
        if (!signal.aborted) {
          console.error(`${i18next.t('nodeMetaError')}: ${e}`);
        }
      });
  };

  const [nodeMetaData, setNodeMetadata] = useState<INodeMetadata[]>([]);

  const [
    indexToDisplayHierarchyUpTo,
    setIndexToDisplayHierarchyUpTo
  ] = useState(20);

  const pseudoLazyLoadData = (node: TreeNode) => {
    fetchMetaData(node.data.rootNodeId, node.data.id);

    const rowIndex = rootOrganisationData.findIndex(
      hierarchyNode => hierarchyNode.key === node.key
    );

    if (rowIndex + lazyRowBufferDistance > indexToDisplayHierarchyUpTo) {
      setIndexToDisplayHierarchyUpTo(rowIndex + lazyRowBufferDistance);
    }
  };

  const limitHierarchyToIndex = (hierarchy: TreeNode[]) => {
    return hierarchy ? hierarchy.slice(0, indexToDisplayHierarchyUpTo + 1) : [];
  };

  const handleRightClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    clickedNode: TreeNode<IHierarchyBuilderNodeData>
  ) => {
    event.preventDefault();
    // removeNodeFromHierarchy(clickedNode);
  };

  const removeNodeFromHierarchy = (
    targetNode: TreeNode<IHierarchyBuilderNodeData>
  ) => {
    setTreeNodeHierarchy(prev => {
      const prevTreeNodeHierarchy = [...prev];
      let root = prevTreeNodeHierarchy.find(
        rootNode => rootNode.data?.id === targetNode.data?.rootNodeId
      );
      if (root) {
        recursivelyRemoveNode(
          targetNode,
          targetNode.data?.path ? targetNode.data?.path : [],
          root
        );
        const rootIndex = prevTreeNodeHierarchy.findIndex(
          rootNode => rootNode.key === targetNode.data?.rootNodeId
        );
        prevTreeNodeHierarchy.splice(rootIndex, 1, root);
      }

      return prevTreeNodeHierarchy;
    });
  };

  const recursivelyRemoveNode = (
    targetNode: TreeNode<IHierarchyBuilderNodeData>,
    path: string[],
    parentNode: TreeNode<IHierarchyBuilderNodeData>
  ) => {
    let foundNodeIndex = parentNode.children.findIndex(childNode => {
      return childNode.key === targetNode.data?.id;
    });
    if (foundNodeIndex === -1) {
      parentNode.children.forEach(childNode => {
        recursivelyRemoveNode(
          targetNode,
          path.slice(1, path.length),
          childNode
        );
      });
    } else {
      parentNode.children.splice(foundNodeIndex, 1);
      parentNode.leaf = parentNode.children.length === 0;
    }

    return parentNode;
  };

  const EllipsisContextMenu = (props: {
    rowData: TreeNode<IHierarchyBuilderNodeData>;
  }) => {
    const [warningModalDelete, setWarningModalDelete] = useState<boolean>(
      false
    );
    return (
      <ContextMenu>
        <ContextMenu.ContextRow
          onClickRow={() => {
            setWarningModalDelete(true);
          }}
          disabled={isNodeRootNode(props.rowData)}
        >
          {i18next.t('delete')}
        </ContextMenu.ContextRow>
        <Modal
          open={warningModalDelete}
          size="large"
          onClose={() => setWarningModalDelete(false)}
        >
          <Modal.Header>{i18next.t('warning')}:</Modal.Header>
          <Modal.Content>
            {i18next.t('deleteConfirmation')} {props.rowData.data?.name}?
          </Modal.Content>
          <Modal.Footer>
            <Button type="primary" onClick={() => setWarningModalDelete(false)}>
              {i18next.t('cancel')}
            </Button>
            <Button type="secondary" onClick={() => deleteNode(props.rowData)}>
              {i18next.t('deleteSure')}
            </Button>
          </Modal.Footer>
        </Modal>
      </ContextMenu>
    );
  };

  const [nodeBeingDeleted, setNodeBeingDeleted] = useState<boolean>(false);

  const deleteNode = (rowData: TreeNode<IHierarchyBuilderNodeData>) => {
    const pathString = getNodePath(rowData);

    setNodeBeingDeleted(true);

    authService
      .authFetch(
        `${authService.BASE_URL}/api/v1/organisations/LCBS/${rowData.data?.rootNodeId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify([
            {
              op: 'remove',
              path: pathString
            }
          ])
        }
      )
      .then(res => {
        if (res.ok) {
          removeNodeFromHierarchy(rowData);
        } else {
          throw new Error('Could not delete node');
        }
      })
      .catch(e => {
        toasts.error(i18next.t('deleteNodeError'));
      })
      .finally(() => {
        setNodeBeingDeleted(false);
      });
  };

  const [selectedNodeForEdit, setSelectedNodeForEdit] = useState<
    TreeNode<IHierarchyBuilderNodeData>
  >();

  return (
    <div className="page-content-container top-padded hierarchy-builder-page">
      <div className="hb-table">
        <SplitPanelUtil>
          <SplitPanelUtil.Table>
            <HierarchyTable
              loading={isLoadingRootOrganisationData || nodeBeingDeleted}
              value={limitHierarchyToIndex(
                injectMetaData(rootOrganisationData)
              )}
              onExpand={onExpandRow}
              onRowVisible={pseudoLazyLoadData}
              header={
                <>
                  <div style={{ flexGrow: 1, textAlign: 'left' }}>
                    <button
                      className="header-item-wrapper button"
                      onClick={() => setCreateNewOrgModelOpen(true)}
                    >
                      <Icon
                        className="text-adjacent"
                        root="common"
                        size="medium"
                        name="badge-plus"
                      />
                      <span>{i18next.t('newOrg')}</span>
                    </button>
                    <button
                      disabled={selectedNodes.length !== 1}
                      className="header-item-wrapper button"
                      onClick={() => setAddNewNodesOpen(true)}
                    >
                      <Icon
                        className="text-adjacent"
                        root="common"
                        size="medium"
                        name="badge-plus"
                      />
                      <span>{i18next.t('addNodesSites')}</span>
                    </button>
                  </div>
                  <div
                    className="header-item-wrapper"
                    onClick={() => {
                      history.push('/app/feature/organisations');
                    }}
                  >
                    <Icon root="common" size="medium" name="table" />
                  </div>
                </>
              }
            >
              <Column
                field="name"
                className="flex-vertically-centered remove-right-pad"
                renderer={(cellData: any, rowData: any) => {
                  return (
                    <>
                      {nodeCell(cellData, rowData)}
                      <hr className="dashed-rule" />
                    </>
                  );
                }}
                expander
              />
              <Column
                field="actions"
                className="action-column"
                renderer={(cellData: any, rowData: any) => (
                  <>
                    <Icon
                      className="action-button"
                      root="common"
                      size="medium"
                      name="edit"
                      onClick={() => {
                        setSelectedNodeForEdit(rowData);
                      }}
                    />
                    <Icon
                      onClick={() => {
                        setSelectedNodes([rowData]);
                        setAddNewNodesOpen(true);
                      }}
                      className="action-button"
                      root="common"
                      size="medium"
                      name="badge-plus"
                    />
                    <ContextMenuWrapper>
                      <ContextMenuWrapper.MenuSection>
                        <EllipsisContextMenu rowData={rowData} />
                      </ContextMenuWrapper.MenuSection>
                      <ContextMenuWrapper.Reference>
                        <Icon
                          className="action-button fix-ellipse "
                          root="common"
                          size="medium"
                          name="ellipsis-vertical"
                        />
                      </ContextMenuWrapper.Reference>
                    </ContextMenuWrapper>
                  </>
                )}
              />
            </HierarchyTable>
          </SplitPanelUtil.Table>
          {selectedNodeForEdit ? (
            <HierarchyBuilderEditSidePanel
              onClose={() => setSelectedNodeForEdit(undefined)}
              onFinish={() => {
                setSelectedNodeForEdit(undefined);
                const rootNodeId = selectedNodeForEdit.data
                  ? selectedNodeForEdit.data.rootNodeId
                  : '';
                const nodeId = selectedNodeForEdit.data
                  ? selectedNodeForEdit.data.id
                  : '';
                fetchMetaData(rootNodeId, nodeId).then(() =>
                  refetchNodeTypeAndName(rootNodeId, nodeId)
                );
              }}
              selectedNode={selectedNodeForEdit}
            />
          ) : null}
        </SplitPanelUtil>
      </div>
      <Modal
        open={createNewOrgModelOpen}
        closeIcon={true}
        onClose={() => setCreateNewOrgModelOpen(false)}
        size={'small'}
        className="overflow-friendly content-wide modal-design-1"
      >
        <Modal.Header>{i18next.t('newOrg')}</Modal.Header>
        <Modal.Content>
          <div>
            <Input
              label={i18next.t('name')}
              onChange={change => setCreateNewOrgNameField(change)}
            />
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button
            type="secondary"
            onClick={() => setCreateNewOrgModelOpen(false)}
          >
            {i18next.t('cancel')}
          </Button>
          <Button
            disabled={createNewOrgModelSending}
            loading={createNewOrgModelSending}
            onClick={createNewOrganisation}
          >
            {i18next.t('create')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        open={addNewNodesOpen}
        closeIcon={true}
        onClose={() => setAddNewNodesOpen(false)}
        size={'small'}
        className="overflow-friendly content-wide modal-design-1"
      >
        <Modal.Header>{i18next.t('addNodesSites')}</Modal.Header>
        <Modal.Content>
          <div>
            <Input
              onChange={value => {
                setAddNewNodeNameField(value);
              }}
              label={i18next.t('name')}
              error=""
            />
          </div>
          <div>
            <Select
              label={i18next.t('type')}
              error=""
              style={{ textTransform: 'capitalize' }}
              options={nodeTypes.map(type => {
                return {
                  text: type,
                  value: type
                };
              })}
              onChange={change => {
                setNewNodeType(change);
              }}
            />
            <div>
              {i18next.t('nodesWillBeCreatedAt')}
              <div>
                <Icon
                  root="common"
                  size="small"
                  name="buildings"
                  className="text-adjacent"
                />
                {selectedNodes[0] ? selectedNodes[0].data?.name : 'Unknown'}
              </div>
            </div>
            <div></div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button type="secondary" onClick={() => setAddNewNodesOpen(false)}>
            {i18next.t('cancel')}
          </Button>
          <Button
            disabled={
              addNewNodesOpenSending ||
              !newNodeType ||
              !isNewNodeNameFieldValid()
            }
            loading={addNewNodesOpenSending}
            onClick={() =>
              selectedNodes[0] ? addNewNode(selectedNodes[0]) : undefined
            }
          >
            {i18next.t('create')}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* <div
        className="drop-zone"
        onDragOver={event => {
          event.preventDefault();
        }}
        onDrop={event => {
          var data = JSON.parse(
            event.dataTransfer.getData('text')
          ) as TreeNode<IHierarchyBuilderNodeData>[];
          setClipboardNodes(clipboardNodesPrev => [
            ...clipboardNodesPrev.filter(
              node => !data.find(dragged => node.data.id === dragged.data.id)
            ),
            ...data
          ]);
          setSelectedNodes([]);
        }}
      >
        <div>
          <div>Mock clipboard</div>
          {clipboardNodes.map(node => (
            <>
              <div>{nodeCell(node.data.name, node)}</div>
              <br />
            </>
          ))}
        </div>
      </div> */}
    </div>
  );
};

export default consumeAPIs(HierarchyBuilder);
